import { useState } from 'react';
import { Link, useParams } from 'wouter';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useStoreProfileDetail } from '@/features/store-profiles/hooks/useStoreProfilesData';
import { ErrorState } from '@/components/ui/error-state';
import { RetailerView } from '@/features/store-profiles/components/retailer/RetailerView';
import { ManageTwinWorkspace } from '@/features/store-profiles/components/manage/ManageTwinWorkspace';

type ViewMode = 'retailer' | 'manage';

/**
 * Store profile detail — retailer-first by default. Administrators enter
 * Manage Twin for setup, mapping and publish work.
 */
export default function StoreProfileDetailPage() {
  const params = useParams<{ storeId: string }>();
  const storeIdParam = params.storeId ?? '';
  const { profile, isLoading, isError, error } = useStoreProfileDetail(storeIdParam);
  const [viewMode, setViewMode] = useState<ViewMode>('retailer');

  const numericStoreId = profile?.storeId ?? (/^\d+$/.test(storeIdParam) ? parseInt(storeIdParam, 10) : undefined);

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" aria-label="Loading store profile" />
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <ErrorState
          title="Store profile not found"
          message={error instanceof Error ? error.message : `No store found for id "${storeIdParam}"`}
        />
        <Link href="/super-admin/store-profiles">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to store directory
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 overflow-x-hidden">
      <Link href="/super-admin/store-profiles">
        <Button variant="ghost" size="sm" className="mb-3 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Store Profiles
        </Button>
      </Link>

      {viewMode === 'retailer' ? (
        <RetailerView
          profile={profile}
          storeIdParam={storeIdParam}
          onManageTwin={() => setViewMode('manage')}
        />
      ) : (
        <ManageTwinWorkspace
          profile={profile}
          storeId={numericStoreId}
          storeIdParam={storeIdParam}
          onExit={() => setViewMode('retailer')}
        />
      )}
    </div>
  );
}
